import React, { useEffect, useRef, useState } from 'react';
import { useThrottle } from '@/hooks/useThrottle';
import { Loader2 } from 'lucide-react';

interface WaveBackgroundProps {
  isLoading: boolean;
  className?: string;
  children?: React.ReactNode;
}

const WaveBackground: React.FC<WaveBackgroundProps> = ({ isLoading, className = "", children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const throttledMousePosition = useThrottle(mousePosition, 100); // Increased throttle time
  
  useEffect(() => {
    // Calculate the content area dimensions (excluding topbar and sidebar)
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      
      // Get the bounding rectangle of the content area
      const rect = containerRef.current.getBoundingClientRect();
      
      // Calculate the mouse position relative to the content area
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      
      setMousePosition({ x, y });
    };
    
    // Set CSS variables for positioning
    document.documentElement.style.setProperty('--topbar-height', '87px');
    document.documentElement.style.setProperty('--sidebar-width', '240px');
    
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  // Calculate wave transformations based on mouse position with reduced sensitivity
  const getWaveStyle = (index: number) => {
    const baseDelay = index * 2;
    const xOffset = (throttledMousePosition.x - 50) * 0.02; // Reduced sensitivity
    const yOffset = (throttledMousePosition.y - 50) * 0.01; // Reduced sensitivity
    
    return {
      transform: `translate(${xOffset * (index + 1)}%, ${yOffset * (index + 1)}%)`,
      animationDelay: `${baseDelay}s`,
    };
  };
  
  return (
    <div className="relative w-full h-full" ref={containerRef}>
      <div className="wave-background-container absolute inset-0 bg-white dark:bg-gray-900">
        <div className="waves-container">
          <div className="wave wave-1 dark:bg-gray-800/50" style={getWaveStyle(0)}></div>
          <div className="wave wave-2 dark:bg-gray-800/40" style={getWaveStyle(2)}></div>
          <div className="wave wave-3 dark:bg-gray-800/30" style={getWaveStyle(1)}></div>
          <div className="wave wave-4 dark:bg-gray-800/20" style={getWaveStyle(3)}></div>
        </div>
      </div>
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/5 dark:bg-black/20 backdrop-blur-[1px]">
          <Loader2 className="w-16 h-16 text-[#FF4A8E] animate-spin" />
        </div>
      ) : (
        <div className="relative h-full">{children}</div>
      )}
    </div>
  );
};

export default WaveBackground;
